import api from "./api";
import { API_ENDPOINTS } from "../utils/constants";

// Helper function to check if data contains actual File objects
const hasFiles = (userData) => {
  // Check if agreements contain File objects
  if (userData.agreements && Array.isArray(userData.agreements)) {
    for (let agreement of userData.agreements) {
      // Check if agreementDocument is a File object
      if (agreement.agreementDocument instanceof File) {
        return true;
      }
      // Check if agreementDocument is an array with File objects
      if (Array.isArray(agreement.agreementDocument)) {
        const hasFile = agreement.agreementDocument.some(
          (item) => item instanceof File
        );
        if (hasFile) return true;
      }
    }
  }

  // Check if documents contain File objects
  if (userData.documents && Array.isArray(userData.documents)) {
    for (let doc of userData.documents) {
      // Check if documentFile is a File object
      if (doc.documentFile instanceof File) {
        return true;
      }
      // Check if documentFile is an array with File objects
      if (Array.isArray(doc.documentFile)) {
        const hasFile = doc.documentFile.some((item) => item instanceof File);
        if (hasFile) return true;
      }
    }
  }

  return false;
};

// Helper function to extract File from agreement/document object
const extractFile = (fileData) => {
  if (fileData instanceof File) {
    return fileData;
  }

  // If it's an array, find the File object
  if (Array.isArray(fileData)) {
    for (let item of fileData) {
      if (item instanceof File) {
        return item;
      }
    }
  }

  return null;
};

// Helper function to convert user data to FormData
const createFormData = (userData) => {
  const formData = new FormData();

  // Add regular fields
  const regularFields = [
    "name",
    "email",
    "phone",
    "signupip",
    "signupdeviceid",
    "isresearchanalyst",
    "isblocked",
    "age",
    "gender",
    "prefsectors",
    "prefindustries",
    "prefstocks",
    "needguidance",
    "experienceinmarket",
    "preferenceonapp",
    "timelinecontent",
  ];

  regularFields.forEach((field) => {
    if (userData[field] !== undefined && userData[field] !== null) {
      formData.append(field, userData[field]);
    }
  });

  // Process agreements array
  if (userData.agreements && Array.isArray(userData.agreements)) {
    const agreementsData = userData.agreements.map((agreement) => {
      const { agreementDocument, ...agreementWithoutFile } = agreement;

      // If agreementDocument is a string (existing file path), keep it
      if (typeof agreementDocument === "string") {
        return { ...agreementWithoutFile, agreementDocument };
      }

      // Don't include File objects in JSON
      return agreementWithoutFile;
    });

    formData.append("agreements", JSON.stringify(agreementsData));

    // Add agreement files separately
    userData.agreements.forEach((agreement, index) => {
      const file = extractFile(agreement.agreementDocument);
      if (file) {
        formData.append(`agreements[${index}][agreementDocument]`, file);
      }
    });
  }

  // Process documents array
  if (userData.documents && Array.isArray(userData.documents)) {
    const documentsData = userData.documents.map((doc) => {
      const { documentFile, ...docWithoutFile } = doc;

      // If documentFile is a string (existing file path), keep it
      if (typeof documentFile === "string") {
        return { ...docWithoutFile, documentFile };
      }

      // Don't include File objects in JSON
      return docWithoutFile;
    });

    formData.append("documents", JSON.stringify(documentsData));

    // Add document files separately
    userData.documents.forEach((doc, index) => {
      const file = extractFile(doc.documentFile);
      if (file) {
        formData.append(`documents[${index}][documentFile]`, file);
      }
    });
  }

  return formData;
};

export const userService = {
  // Get all users
  getAllUsers: async (limit, offset) => {
    try {
      // params = { limit: 50, offset: 0, search: 'yash', etc. }
      const response = await api.get(
        `${API_ENDPOINTS.USERS}?limit=${limit}&offset=${offset}`,
        { limit, offset }
      );


      // Axios auto-parses JSON, so return only the data portion
      return response.data;
    } catch (error) {
      console.error("Error fetching users:", error);
      throw error;
    }
  },

  // ra users
  getRaUsers: async (limit, offset) => {
    try {
      // params = { limit: 50, offset: 0, search: 'yash', etc. }
      const response = await api.get(
        `${API_ENDPOINTS.RAUSERS}?limit=${limit}&offset=${offset}`,
        { limit, offset }
      );


      // Axios auto-parses JSON, so return only the data portion
      return response.data;
    } catch (error) {
      console.error("Error fetching ra users:", error);
      throw error;
    }
  },

  // Get user by ID
  getUserById: async (id) => {
    try {
      const response = await api.get(API_ENDPOINTS.USER_BY_ID(id));
      return response;
    } catch (error) {
      throw error;
    }
  },

  // Create new user
  createUser: async (userData) => {
    try {

      // Check if userData contains files
      if (hasFiles(userData)) {

        // Convert to FormData for file upload
        const formData = createFormData(userData);

        // Send as multipart/form-data
        const response = await api.post(API_ENDPOINTS.USERS, formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
          timeout: 120000, // 2 minutes
        });

        return response;
      } else {
        const response = await api.post(API_ENDPOINTS.USERS, userData);
        return response;
      }
    } catch (error) {
      console.error("Error creating user:", error);
      throw error;
    }
  },

  // Update user
  updateUser: async (id, userData) => {
    try {

      // Check if userData contains files
      if (hasFiles(userData)) {

        // Convert to FormData for file upload
        const formData = createFormData(userData);

        // Send as multipart/form-data
        const response = await api.put(API_ENDPOINTS.USER_BY_ID(id), formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
          timeout: 120000, // 2 minutes
        });

        return response;
      } else {
        const response = await api.put(API_ENDPOINTS.USER_BY_ID(id), userData);
        return response;
      }
    } catch (error) {
      console.error("Error updating user:", error);
      throw error;
    }
  },

  // Delete user
  deleteUser: async (id) => {
    try {
      const response = await api.delete(API_ENDPOINTS.USER_BY_ID(id));
      return response;
    } catch (error) {
      throw error;
    }
  },

  // Search users
  searchUsers: async (query, filters = {}) => {
    try {
      const params = { search: query, ...filters };
      const response = await api.get(API_ENDPOINTS.USERS, { params });
      return response;
    } catch (error) {
      throw error;
    }
  },

  // Update profile specifics
  updateUserProfile: async (id, userData) => {
    try {
      // Backend route: router.put("/users/:id", updateUserProfile);
      // This is under /api/users
      const response = await api.put(`${API_ENDPOINTS.USERS}/users/${id}`, userData);
      return response;
    } catch (error) {
      console.error("Error updating user profile:", error);
      throw error;
    }
  },

  // Change password
  changePassword: async (id, passwords) => {
    try {
      // Backend route: router.put("/change-password/:id", changeUserPassword);
      const response = await api.put(`${API_ENDPOINTS.USERS}/change-password/${id}`, passwords);
      return response;
    } catch (error) {
      console.error("Error changing password:", error);
      throw error;
    }
  },
};
